import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Zap, LayoutDashboard, CreditCard, BarChart2, Headphones,
  CheckCircle2, ArrowRight, Loader2, LogOut, ChevronUp, TrendingUp,
  Globe, Star, Clock, MessageSquare, AlertCircle, X, ExternalLink,
  Package, Calendar, ShieldCheck, Send
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = {
  starter:  { name: "Starter",  color: "text-blue-400",   bg: "bg-blue-400/10",   border: "border-blue-400/20",  monthly: 39.99, setup: 199 },
  business: { name: "Business", color: "text-cyan-400",   bg: "bg-cyan-400/10",   border: "border-cyan-400/20",  monthly: 59.99, setup: 249 },
  premium:  { name: "Premium",  color: "text-purple-400", bg: "bg-purple-400/10", border: "border-purple-400/20", monthly: 99,    setup: 499 },
};

// ── Auth / Token handling ────────────────────────────────────────────────────
function usePortalAuth(accessToken) {
  const [authToken, setAuthToken] = useState(() => localStorage.getItem("portal_jwt"));
  const [account, setAccount] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState("");

  const login = useCallback(async (token) => {
    setAuthLoading(true);
    setAuthError("");
    try {
      const res = await axios.post(`${API}/portal/auth`, { access_token: token });
      localStorage.setItem("portal_jwt", res.data.token);
      setAuthToken(res.data.token);
      setAccount(res.data.account);
    } catch (err) {
      setAuthError(err.response?.data?.detail || "Ungültiger Zugangscode. Bitte wenden Sie sich an signalSolution.");
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("portal_jwt");
    setAuthToken(null);
    setAccount(null);
  }, []);

  // Auto-login via URL token
  useEffect(() => {
    if (accessToken) {
      login(accessToken);
    } else if (authToken) {
      // Validate existing JWT
      axios.get(`${API}/portal/me`, { headers: { Authorization: `Bearer ${authToken}` } })
        .then(res => { setAccount(res.data); setAuthLoading(false); })
        .catch(() => { logout(); setAuthLoading(false); });
    } else {
      setAuthLoading(false);
    }
  }, [accessToken]); // eslint-disable-line

  return { authToken, account, authLoading, authError, login, logout };
}

// ── Mini Bar Chart ─────────────────────────────────────────────────────────
function BarChart({ data }) {
  const max = Math.max(...data.map(d => d.visits), 1);
  return (
    <div className="flex items-end gap-1.5 h-20">
      {data.map((d, i) => (
        <div key={i} className="flex flex-col items-center gap-1 flex-1">
          <div
            className="w-full bg-cyan-400/30 rounded-sm hover:bg-cyan-400/50 transition-colors"
            style={{ height: `${(d.visits / max) * 68}px`, minHeight: 4 }}
            title={`${d.visits} Besuche`}
          />
          <span className="text-[9px] text-white/30">{d.day}</span>
        </div>
      ))}
    </div>
  );
}

// ── Ticket Status Badge ────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = {
    offen: "bg-amber-400/15 text-amber-400 border-amber-400/20",
    "in bearbeitung": "bg-blue-400/15 text-blue-400 border-blue-400/20",
    gelöst: "bg-green-400/15 text-green-400 border-green-400/20",
    geschlossen: "bg-white/10 text-white/40 border-white/10",
  }[status] || "bg-white/5 text-white/40 border-white/5";
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${cfg}`}>{status}</span>
  );
}

// ── Portal App (authenticated) ───────────────────────────────────────────────
function PortalApp({ account, authToken, logout }) {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: "", message: "", priority: "normal" });
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [currentAccount, setCurrentAccount] = useState(account);

  const headers = { Authorization: `Bearer ${authToken}` };

  const plan = PLANS[currentAccount.plan] || PLANS.starter;

  const refreshAccount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portal/me`, { headers });
      setCurrentAccount(res.data);
    } catch {}
  }, [authToken]); // eslint-disable-line

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API}/portal/stats`, { headers });
      setStats(res.data);
    } catch {}
    setStatsLoading(false);
  }, [authToken]); // eslint-disable-line

  const loadTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portal/tickets`, { headers });
      setTickets(res.data);
    } catch {}
  }, [authToken]); // eslint-disable-line

  // Load on tab change
  useEffect(() => {
    if (tab === "stats") loadStats();
    if (tab === "support") loadTickets();
  }, [tab]); // eslint-disable-line

  // Check for Stripe session_id in URL (payment return)
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (sid) {
      setSessionId(sid);
      pollPaymentStatus(sid);
    }
  }, []); // eslint-disable-line

  const pollPaymentStatus = async (sid, attempt = 0) => {
    if (attempt > 8) return;
    try {
      const res = await axios.get(`${API}/portal/checkout/status/${sid}`, { headers });
      setPaymentStatus(res.data);
      if (res.data.payment_status === "paid") {
        toast.success("Zahlung erfolgreich! Ihr Abo ist jetzt aktiv.");
        refreshAccount();
        return;
      }
    } catch {}
    setTimeout(() => pollPaymentStatus(sid, attempt + 1), 2000);
  };

  const handleCheckout = async (targetPlan, type = "monthly") => {
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/portal/checkout`, {
        plan: targetPlan,
        payment_type: type,
        origin_url: window.location.origin,
      }, { headers });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Zahlungsvorgang");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject || !newTicket.message) return;
    setSubmittingTicket(true);
    try {
      await axios.post(`${API}/portal/tickets`, newTicket, { headers });
      toast.success("Ihr Support-Ticket wurde erstellt. Wir melden uns in 24h.");
      setNewTicket({ subject: "", message: "", priority: "normal" });
      loadTickets();
    } catch {
      toast.error("Fehler beim Erstellen des Tickets");
    } finally {
      setSubmittingTicket(false);
    }
  };

  const tabs = [
    { id: "dashboard", icon: <LayoutDashboard size={16} />, label: "Dashboard" },
    { id: "abo", icon: <CreditCard size={16} />, label: "Mein Abo" },
    { id: "stats", icon: <BarChart2 size={16} />, label: "Besucher" },
    { id: "support", icon: <Headphones size={16} />, label: "Support" },
  ];

  const statusColor = {
    aktiv: "text-green-400",
    pending: "text-amber-400",
    inaktiv: "text-red-400",
  }[currentAccount.subscription_status] || "text-white/40";

  return (
    <div className="min-h-screen bg-[#050911] text-white">
      {/* Payment success banner */}
      {paymentStatus?.payment_status === "paid" && (
        <div className="bg-green-500/15 border-b border-green-500/20 px-6 py-3 text-center text-sm text-green-400 flex items-center justify-center gap-2">
          <CheckCircle2 size={14} />
          Zahlung erfolgreich — Ihr Abo ist jetzt aktiv!
        </div>
      )}

      {/* Header */}
      <header className="border-b border-white/5 bg-[#050911]/95 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-cyan-400 flex items-center justify-center">
              <Zap size={13} className="text-slate-900 fill-slate-900" />
            </div>
            <span className="font-bold text-sm text-white">signal<span className="text-cyan-400">Solution</span></span>
            <span className="hidden sm:block text-white/20 ml-2 text-xs">Kunden-Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <p className="text-xs font-medium text-white">{currentAccount.company_name}</p>
              <p className="text-xs text-white/40">{currentAccount.email}</p>
            </div>
            <button onClick={logout} className="text-white/40 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/5" title="Abmelden">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-white/3 border border-white/5 rounded-xl p-1">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`portal-tab-${t.id}`}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                tab === t.id
                  ? "bg-[#0D1421] text-white shadow-sm border border-white/5"
                  : "text-white/50 hover:text-white/80"
              }`}
            >
              {t.icon}
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        {/* ── DASHBOARD ── */}
        {tab === "dashboard" && (
          <div className="space-y-6">
            {/* Welcome */}
            <div className="bg-[#0A1220] border border-white/5 rounded-2xl p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-white/40 text-sm mb-1">Willkommen zurück</p>
                  <h1 className="font-black text-2xl text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {currentAccount.contact_name || currentAccount.company_name}
                  </h1>
                  <p className="text-white/50 text-sm mt-1">{currentAccount.company_name}</p>
                </div>
                <div className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${plan.bg} ${plan.color} ${plan.border}`}>
                  {plan.name}
                </div>
              </div>
            </div>

            {/* Status Grid */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-[#0A1220] border border-white/5 rounded-xl p-5">
                <Package size={18} className="text-cyan-400 mb-3" />
                <p className="text-xs text-white/40 uppercase tracking-widest mb-1">Ihr Paket</p>
                <p className="font-bold text-white">{plan.name}</p>
                <p className="text-xs text-white/40 mt-0.5">{currentAccount.monthly_price}€/Monat</p>
              </div>
              <div className="bg-[#0A1220] border border-white/5 rounded-xl p-5">
                <ShieldCheck size={18} className={`mb-3 ${statusColor}`} />
                <p className="text-xs text-white/40 uppercase tracking-widest mb-1">Status</p>
                <p className={`font-bold capitalize ${statusColor}`}>{currentAccount.subscription_status}</p>
                {currentAccount.subscription_status === "pending" && (
                  <p className="text-xs text-amber-400/70 mt-0.5">Zahlung ausstehend</p>
                )}
              </div>
              <div className="bg-[#0A1220] border border-white/5 rounded-xl p-5">
                <Headphones size={18} className="text-purple-400 mb-3" />
                <p className="text-xs text-white/40 uppercase tracking-widest mb-1">Support</p>
                <p className="font-bold text-white">{tickets.length > 0 ? `${tickets.filter(t => t.status === "offen").length} offen` : "Kein Ticket"}</p>
                <button onClick={() => setTab("support")} className="text-xs text-cyan-400 hover:text-cyan-300 mt-0.5 transition-colors">
                  Ticket erstellen →
                </button>
              </div>
            </div>

            {/* If pending payment */}
            {currentAccount.subscription_status === "pending" && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-white mb-1">Abo-Zahlung ausstehend</p>
                    <p className="text-sm text-white/55 mb-4">Um alle Funktionen nutzen zu können, aktivieren Sie Ihr {plan.name}-Paket jetzt.</p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => handleCheckout(currentAccount.plan, "setup")}
                        disabled={checkoutLoading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-cyan-400 text-slate-900 font-bold rounded-xl hover:bg-cyan-300 transition-all text-sm shadow-lg shadow-cyan-400/20"
                      >
                        {checkoutLoading ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                        Einrichtungsgebühr zahlen ({(PLANS[currentAccount.plan]?.setup || 199)}€)
                      </button>
                      <button
                        onClick={() => handleCheckout(currentAccount.plan, "monthly")}
                        disabled={checkoutLoading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-white/5 text-white font-medium rounded-xl hover:bg-white/10 transition-all border border-white/10 text-sm"
                      >
                        Monatsbeitrag zahlen ({currentAccount.monthly_price}€)
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick links */}
            <div className="grid sm:grid-cols-2 gap-4">
              <button onClick={() => setTab("stats")} className="bg-[#0A1220] border border-white/5 rounded-xl p-5 text-left hover:border-white/10 transition-all group">
                <BarChart2 size={18} className="text-cyan-400 mb-3" />
                <p className="font-semibold text-white mb-1">Besucher-Statistiken</p>
                <p className="text-sm text-white/40 group-hover:text-white/55 transition-colors">Sehen Sie wie viele Menschen Ihre Website besuchen.</p>
              </button>
              <button onClick={() => setTab("support")} className="bg-[#0A1220] border border-white/5 rounded-xl p-5 text-left hover:border-white/10 transition-all group">
                <MessageSquare size={18} className="text-purple-400 mb-3" />
                <p className="font-semibold text-white mb-1">Support kontaktieren</p>
                <p className="text-sm text-white/40 group-hover:text-white/55 transition-colors">Fragen? Änderungswunsch? Wir helfen schnell.</p>
              </button>
            </div>
          </div>
        )}

        {/* ── ABO ── */}
        {tab === "abo" && (
          <div className="space-y-6">
            {/* Current plan */}
            <div className={`bg-[#0A1220] border-2 ${plan.border} rounded-2xl p-6`}>
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Aktuelles Paket</p>
                  <h2 className="font-black text-3xl text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{plan.name}</h2>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-2xl font-bold text-white">{currentAccount.monthly_price}€</span>
                    <span className="text-white/40 text-sm">/Monat</span>
                  </div>
                </div>
                <div className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                  currentAccount.subscription_status === "aktiv"
                    ? "bg-green-400/15 text-green-400 border-green-400/20"
                    : "bg-amber-400/15 text-amber-400 border-amber-400/20"
                }`}>
                  {currentAccount.subscription_status === "aktiv" ? "Aktiv" : "Ausstehend"}
                </div>
              </div>

              {currentAccount.subscription_status === "pending" && (
                <div className="space-y-3">
                  <button
                    onClick={() => handleCheckout(currentAccount.plan, "setup")}
                    disabled={checkoutLoading}
                    data-testid="pay-setup-btn"
                    className="flex items-center gap-2 px-6 py-3 bg-cyan-400 text-slate-900 font-bold rounded-xl hover:bg-cyan-300 transition-all shadow-lg shadow-cyan-400/20 w-full sm:w-auto justify-center"
                  >
                    {checkoutLoading ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />}
                    Einrichtungsgebühr zahlen ({PLANS[currentAccount.plan]?.setup}€)
                  </button>
                  <button
                    onClick={() => handleCheckout(currentAccount.plan, "monthly")}
                    disabled={checkoutLoading}
                    data-testid="pay-monthly-btn"
                    className="flex items-center gap-2 px-6 py-3 bg-white/5 text-white font-medium rounded-xl hover:bg-white/10 transition-all border border-white/10 w-full sm:w-auto justify-center"
                  >
                    Monatsbeitrag zahlen ({currentAccount.monthly_price}€)
                  </button>
                </div>
              )}
            </div>

            {/* Upgrade options */}
            <div>
              <h3 className="font-bold text-white mb-4">Paket upgraden</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {Object.entries(PLANS).map(([key, p]) => {
                  const isCurrentPlan = key === currentAccount.plan;
                  return (
                    <div
                      key={key}
                      className={`bg-[#0A1220] border rounded-xl p-5 ${isCurrentPlan ? `border-2 ${p.border}` : "border-white/5"}`}
                    >
                      <p className={`font-bold text-sm mb-1 ${isCurrentPlan ? p.color : "text-white/60"}`}>{p.name}</p>
                      <div className="flex items-baseline gap-1 mb-3">
                        <span className="text-2xl font-black text-white">{p.monthly}€</span>
                        <span className="text-white/30 text-xs">/mo</span>
                      </div>
                      {isCurrentPlan ? (
                        <span className={`text-xs px-3 py-1 rounded-full border ${p.bg} ${p.color} ${p.border}`}>Ihr Paket</span>
                      ) : (
                        <button
                          onClick={() => handleCheckout(key, "monthly")}
                          disabled={checkoutLoading}
                          className="w-full py-2 text-sm font-medium bg-white/5 text-white/70 hover:bg-white/10 hover:text-white rounded-lg border border-white/10 transition-all"
                        >
                          Wechseln
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Cancellation note */}
            <div className="flex items-start gap-3 p-4 bg-white/3 border border-white/5 rounded-xl">
              <ShieldCheck size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-white/50">
                Monatlich kündbar mit 30 Tagen Frist. Für Kündigungen oder Fragen schreiben Sie uns einfach über den Support-Bereich.
              </p>
            </div>
          </div>
        )}

        {/* ── STATS ── */}
        {tab === "stats" && (
          <div className="space-y-6">
            {statsLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={24} className="animate-spin text-cyan-400" />
              </div>
            ) : stats ? (
              <>
                {/* KPI Row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { label: "Besucher gesamt", value: stats.total_visits.toLocaleString("de-DE"), icon: <TrendingUp size={16} className="text-cyan-400" /> },
                    { label: "Dieser Monat", value: stats.visits_this_month.toLocaleString("de-DE"), icon: <Calendar size={16} className="text-blue-400" /> },
                    { label: "Ø Sitzungsdauer", value: stats.avg_session_min > 0 ? `${stats.avg_session_min} min` : "—", icon: <Clock size={16} className="text-purple-400" /> },
                    { label: "Geräte-Split", value: stats.device_split ? `${stats.device_split[0]?.percent ?? "—"}% PC` : "—", icon: <BarChart2 size={16} className="text-amber-400" /> },
                  ].map(kpi => (
                    <div key={kpi.label} className="bg-[#0A1220] border border-white/5 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">{kpi.icon}<span className="text-xs text-white/40">{kpi.label}</span></div>
                      <p className="font-black text-2xl text-white">{kpi.value}</p>
                    </div>
                  ))}
                </div>

                {/* No real data hint */}
                {!stats.has_real_data && (
                  <div className="flex items-start gap-3 p-4 bg-cyan-400/5 border border-cyan-400/10 rounded-xl">
                    <Globe size={16} className="text-cyan-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-white/55">
                      Noch keine Website-Besucher erfasst. Sobald Ihre Website live geschaltet und freigegeben ist, erscheinen hier die echten Besucherzahlen.
                    </p>
                  </div>
                )}

                {/* Weekly Chart */}
                <div className="bg-[#0A1220] border border-white/5 rounded-xl p-5">
                  <p className="text-sm font-semibold text-white mb-4">Letzte 7 Tage</p>
                  <BarChart data={stats.weekly_chart} />
                </div>

                {/* Sources + Top Pages */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-[#0A1220] border border-white/5 rounded-xl p-5">
                    <p className="text-sm font-semibold text-white mb-4">Traffic-Quellen</p>
                    <div className="space-y-3">
                      {stats.top_sources.map(s => (
                        <div key={s.name}>
                          <div className="flex justify-between text-xs text-white/60 mb-1">
                            <span>{s.name}</span>
                            <span>{s.percent}%</span>
                          </div>
                          <div className="h-1.5 bg-white/5 rounded-full">
                            <div
                              className="h-full bg-cyan-400 rounded-full transition-all duration-700"
                              style={{ width: `${s.percent}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-[#0A1220] border border-white/5 rounded-xl p-5">
                    <p className="text-sm font-semibold text-white mb-4">Top-Seiten</p>
                    {stats.top_pages.length > 0 ? (
                      <div className="space-y-2.5">
                        {stats.top_pages.map(p => (
                          <div key={p.page} className="flex items-center justify-between">
                            <span className="text-sm text-white/60 flex items-center gap-1.5">
                              <Globe size={12} className="text-white/30" /> {p.page}
                            </span>
                            <span className="text-sm font-medium text-white">{p.visits.toLocaleString("de-DE")}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-white/30 text-center py-4">Noch keine Daten</p>
                    )}
                  </div>
                </div>

                {/* Device Split */}
                {stats.device_split && stats.has_real_data && (
                  <div className="bg-[#0A1220] border border-white/5 rounded-xl p-5">
                    <p className="text-sm font-semibold text-white mb-4">Geräte-Verteilung</p>
                    <div className="flex gap-4 items-center">
                      {stats.device_split.map(d => (
                        <div key={d.type} className="flex-1">
                          <div className="flex justify-between text-xs text-white/60 mb-1.5">
                            <span>{d.type}</span><span>{d.percent}%</span>
                          </div>
                          <div className="h-2 bg-white/5 rounded-full">
                            <div
                              className={`h-full rounded-full ${d.type === "Mobile" ? "bg-purple-400" : "bg-cyan-400"}`}
                              style={{ width: `${d.percent}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 text-white/30">Keine Statistiken verfügbar</div>
            )}
          </div>
        )}

        {/* ── SUPPORT ── */}
        {tab === "support" && (
          <div className="space-y-6">
            {/* New ticket form */}
            <div className="bg-[#0A1220] border border-white/5 rounded-2xl p-6">
              <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                <MessageSquare size={18} className="text-purple-400" />
                Neues Support-Ticket
              </h3>
              <form onSubmit={handleSubmitTicket} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-white/30 mb-2">Betreff *</label>
                    <input
                      type="text"
                      value={newTicket.subject}
                      onChange={e => setNewTicket(t => ({ ...t, subject: e.target.value }))}
                      placeholder="z.B. Änderungswunsch Startseite"
                      required
                      data-testid="ticket-subject-input"
                      className="w-full bg-white/5 border border-white/8 rounded-xl px-4 py-3 text-white placeholder-white/25 text-sm focus:outline-none focus:border-cyan-400/50 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-white/30 mb-2">Priorität</label>
                    <select
                      value={newTicket.priority}
                      onChange={e => setNewTicket(t => ({ ...t, priority: e.target.value }))}
                      className="w-full bg-white/5 border border-white/8 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-cyan-400/50 transition-all"
                    >
                      <option value="low">Niedrig</option>
                      <option value="normal">Normal</option>
                      <option value="high">Hoch</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-white/30 mb-2">Ihre Nachricht *</label>
                  <textarea
                    value={newTicket.message}
                    onChange={e => setNewTicket(t => ({ ...t, message: e.target.value }))}
                    placeholder="Beschreiben Sie Ihr Anliegen möglichst genau..."
                    required
                    rows={4}
                    data-testid="ticket-message-input"
                    className="w-full bg-white/5 border border-white/8 rounded-xl px-4 py-3 text-white placeholder-white/25 text-sm focus:outline-none focus:border-cyan-400/50 transition-all resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submittingTicket}
                  data-testid="ticket-submit-btn"
                  className="flex items-center gap-2 px-6 py-3 bg-cyan-400 text-slate-900 font-bold rounded-xl hover:bg-cyan-300 transition-all shadow-lg shadow-cyan-400/15 disabled:opacity-60"
                >
                  {submittingTicket ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  {submittingTicket ? "Wird gesendet..." : "Ticket absenden"}
                </button>
              </form>
            </div>

            {/* Existing tickets */}
            {tickets.length > 0 && (
              <div>
                <h3 className="font-bold text-white mb-3">Meine Tickets</h3>
                <div className="space-y-3">
                  {tickets.map(t => (
                    <div key={t.id} className="bg-[#0A1220] border border-white/5 rounded-xl p-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white text-sm truncate">{t.subject}</p>
                        <p className="text-xs text-white/35 mt-0.5">
                          {new Date(t.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}
                        </p>
                      </div>
                      <StatusBadge status={t.status} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact info */}
            <div className="bg-[#0A1220] border border-white/5 rounded-xl p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-cyan-400/10 flex items-center justify-center text-cyan-400 flex-shrink-0">
                <Headphones size={18} />
              </div>
              <div>
                <p className="font-medium text-white text-sm">Persönlicher Support</p>
                <p className="text-xs text-white/40 mt-0.5">Mo–Fr, 9–18 Uhr · info@signalsolution.de · Antwortzeit: &lt; 24h</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Unauthenticated view ─────────────────────────────────────────────────────
function PortalLogin({ authError, authLoading }) {
  return (
    <div className="min-h-screen bg-[#050911] flex items-center justify-center px-6">
      <div className="max-w-sm w-full text-center">
        <div className="w-12 h-12 rounded-2xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center mx-auto mb-5">
          <Zap size={22} className="text-cyan-400" />
        </div>
        <h1 className="font-black text-2xl text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          signal<span className="text-cyan-400">Solution</span> Portal
        </h1>
        {authLoading ? (
          <div className="flex items-center justify-center gap-2 text-white/50 mt-6">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Anmeldung läuft...</span>
          </div>
        ) : (
          <div className="mt-6">
            {authError ? (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm mb-4">
                {authError}
              </div>
            ) : (
              <p className="text-white/40 text-sm">
                Sie benötigen einen persönlichen Zugangslink von signalSolution.
              </p>
            )}
            <Link
              to="/kontakt#demo-form"
              className="inline-flex items-center gap-2 mt-4 px-6 py-3 bg-cyan-400 text-slate-900 font-bold rounded-xl hover:bg-cyan-300 transition-all text-sm"
            >
              Zugang anfordern <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Portal Page ─────────────────────────────────────────────────────────
export default function KundenPortalPage() {
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get("token");

  const { authToken, account, authLoading, authError, logout } = usePortalAuth(urlToken);

  if (authLoading || (!account && urlToken)) {
    return (
      <div className="min-h-screen bg-[#050911] flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-cyan-400" />
      </div>
    );
  }

  if (!account) {
    return <PortalLogin authError={authError} authLoading={authLoading} />;
  }

  return <PortalApp account={account} authToken={authToken} logout={logout} />;
}
